// client/src/components/Layout/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container, 
  Box, 
  useScrollTrigger, 
  Slide,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  Snackbar,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AdminPanelSettings, 
  Login, 
  Logout, 
  Dashboard, 
  Menu as MenuIcon,
  Home,
  Info,
  ListAlt,
  ContactPhone,
  Gavel,
  MonetizationOn,
  HomeWork,
  Description,
  Assignment,
  CleaningServices,
  Security,
  Fingerprint,
  CardTravel,
  Public,
  Close
} from '@mui/icons-material';

const HideOnScroll = ({ children }) => {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showSecretMessage, setShowSecretMessage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      try {
        const userData = JSON.parse(user);
        setIsLoggedIn(true);
        setUserRole(userData.role);
      } catch (e) {
        setIsLoggedIn(false);
        setUserRole(null);
      }
    } else {
      setIsLoggedIn(false);
      setUserRole(null);
    }
  };

  const handleLogoClick = () => {
    if (isLoggedIn) return;
    
    clickCountRef.current += 1;
    
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 800);
    
    if (clickCountRef.current === 3) {
      clickCountRef.current = 0;
      clearTimeout(clickTimerRef.current);
      
      setShowSecretMessage(true);
      setTimeout(() => setShowSecretMessage(false), 2000);
      
      setTimeout(() => {
        navigate('/login');
      }, 300);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', checkAuthStatus);
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [location.pathname]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogin = () => {
    handleMenuClose();
    setMobileOpen(false);
    navigate('/login');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUserRole(null);
    handleMenuClose();
    setMobileOpen(false);
    navigate('/');
    window.dispatchEvent(new Event('storage'));
  };

  const handleAdminDashboard = () => {
    handleMenuClose();
    setMobileOpen(false);
    navigate('/admin');
  };

  const showAdminButton = isLoggedIn;

  // Menu items pour mobile
  const menuItems = [
    { text: 'Accueil', icon: <Home />, path: '/' },
    { text: 'À propos', icon: <Info />, path: '/about' },
    { text: 'Services', icon: <ListAlt />, path: '/services' },
    { text: 'Contact', icon: <ContactPhone />, path: '/contact' },
  ];

  const serviceItems = [
    { text: 'Consultation juridique', icon: <Gavel />, path: '/consultation' },
    { text: 'Recouvrement de créances', icon: <MonetizationOn />, path: '/recouvrement' },
    { text: 'Services immobiliers', icon: <HomeWork />, path: '/immobilier' },
    { text: 'Formalités administratives', icon: <Description />, path: '/formalites' },
    { text: 'Montage DAO & Marchés publics', icon: <Assignment />, path: '/consultation' },
    { text: 'Nettoyage & Entretien', icon: <CleaningServices />, path: '/consultation' },
    { text: 'Sécurité & Gardiennage', icon: <Security />, path: '/consultation' },
    { text: 'Casier judiciaire', icon: <Fingerprint />, path: '/consultation' },
    { text: 'Passeport', icon: <CardTravel />, path: '/consultation' },
    { text: 'Nationalité', icon: <Public />, path: '/consultation' },
  ];

  const drawer = (
    <Box sx={{ width: 280, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src="/logo2.png" alt="Logo" style={{ width: 40, marginRight: 10 }} />
          <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
            AUJAA GROUP
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem 
            key={item.text} 
            component={Link} 
            to={item.path} 
            onClick={handleDrawerToggle}
            sx={{ borderRadius: 2, mb: 0.5, '&:hover': { backgroundColor: 'rgba(26,35,126,0.05)' } }}
          >
            <ListItemIcon sx={{ color: '#1a237e', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: '#ff9800', fontWeight: 'bold' }}>
          Nos Services
        </Typography>
        {serviceItems.map((item) => (
          <ListItem 
            key={item.text} 
            component={Link} 
            to={item.path} 
            onClick={handleDrawerToggle}
            sx={{ pl: 4, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(26,35,126,0.05)' } }}
          >
            <ListItemIcon sx={{ color: '#1a237e', minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem' }} />
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        {showAdminButton ? (
          <>
            <ListItem onClick={handleAdminDashboard} sx={{ borderRadius: 2, cursor: 'pointer' }}>
              <ListItemIcon sx={{ color: '#1a237e' }}><Dashboard /></ListItemIcon>
              <ListItemText primary="Tableau de bord" />
            </ListItem>
            <ListItem onClick={handleLogout} sx={{ borderRadius: 2, cursor: 'pointer' }}>
              <ListItemIcon sx={{ color: '#d32f2f' }}><Logout /></ListItemIcon>
              <ListItemText primary="Déconnexion" primaryTypographyProps={{ sx: { color: '#d32f2f' } }} />
            </ListItem>
          </>
        ) : (
          <ListItem onClick={handleLogin} sx={{ borderRadius: 2, cursor: 'pointer' }}>
            <ListItemIcon sx={{ color: '#1a237e' }}><Login /></ListItemIcon>
            <ListItemText primary="Administration" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="sticky" 
          sx={{ 
            backgroundColor: scrolled ? 'rgba(26, 35, 126, 0.95)' : '#1a237e',
            backdropFilter: scrolled ? 'blur(10px)' : 'none',
            transition: 'all 0.3s ease',
            boxShadow: scrolled ? 4 : 2
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              {/* Logo avec triple clic */}
              <Box 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={handleLogoClick}
              >
                <Box
                  component={Link}
                  to="/"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1,
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                >
                  <img 
                    src="/logo2.png" 
                    alt="AUJAA GROUP"
                    style={{ width: 40, height: 'auto', display: 'block' }} 
                  />
                </Box>
                <Typography
                  variant="h6"
                  component={Link}
                  to="/"
                  sx={{
                    fontWeight: 700,
                    color: 'inherit',
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #fff, #ffb74d)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ffb74d, #fff)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                    },
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  AUJAA GROUP
                </Typography>
              </Box>
              
              {/* Desktop Menu */}
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button color="inherit" component={Link} to="/" sx={{ '&:hover': { transform: 'translateY(-2px)' } }}>
                    Accueil
                  </Button>
                  <Button color="inherit" component={Link} to="/about" sx={{ '&:hover': { transform: 'translateY(-2px)' } }}>
                    À propos
                  </Button>
                  <Button color="inherit" component={Link} to="/services" sx={{ '&:hover': { transform: 'translateY(-2px)' } }}>
                    Services
                  </Button>
                  <Button color="inherit" component={Link} to="/contact" sx={{ '&:hover': { transform: 'translateY(-2px)' } }}>
                    Contact
                  </Button>
                  <Button 
                    color="secondary" 
                    variant="contained" 
                    component={Link} 
                    to="/consultation"
                    sx={{ 
                      ml: 2,
                      animation: 'pulse 2s infinite',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
                    }}
                  >
                    Consultation
                  </Button>
                  
                  {showAdminButton && (
                    <Tooltip title="Tableau de bord administrateur">
                      <IconButton 
                        onClick={handleMenuOpen}
                        sx={{ 
                          ml: 2,
                          color: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          '&:hover': {
                            backgroundColor: 'rgba(255,152,0,0.3)',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Badge color="secondary" variant="dot">
                          <AdminPanelSettings />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && showAdminButton}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        borderRadius: 3,
                        minWidth: 240,
                        boxShadow: 4,
                        overflow: 'visible',
                      },
                    }}
                  >
                    <MenuItem disabled sx={{ opacity: 1, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AdminPanelSettings fontSize="small" sx={{ color: '#1a237e' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {(() => {
                              try {
                                const user = localStorage.getItem('user');
                                if (user) {
                                  const userData = JSON.parse(user);
                                  return userData.fullName || userData.username || 'Admin';
                                }
                              } catch (e) {}
                              return 'Administrateur';
                            })()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {userRole === 'super_admin' ? 'Super Admin' : 'Administrateur'}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem onClick={handleAdminDashboard} sx={{ gap: 1, mt: 1 }}>
                      <Dashboard fontSize="small" sx={{ color: '#1a237e' }} />
                      Tableau de bord
                    </MenuItem>
                    <MenuItem onClick={handleLogout} sx={{ gap: 1, color: '#d32f2f' }}>
                      <Logout fontSize="small" />
                      Déconnexion
                    </MenuItem>
                  </Menu>
                </Box>
              )}

              {/* Mobile Menu Icon */}
              {isMobile && (
                <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>

      {/* Message secret toast */}
      <Snackbar
        open={showSecretMessage}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1a237e, #283593)',
            color: 'white',
            '& .MuiAlert-icon': { color: '#ff9800' }
          }}
        >
          🔐 Accès administration... (triple clic magique !)
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;